import { AlertFactory, AlertState } from 'alerts/Alert'
import Member from 'iam/models/Member'
import ModalViewModel from 'infra/ModalViewModel'
import Job from 'jobs/Job'
import JobStore from 'jobs/JobStore'
import { action, observable, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import { CursorPaged, CursorPageQuery, Id } from 'types/definitions'
import { required, validationContext } from 'validx'
import { BehaviorSubject } from 'rxjs'
import { FindTeamMembers } from 'iam/TeamMemberStore'

export default class AddClientsAsTeamMemberViewModel extends ModalViewModel {
  /**
   * Tracks whether the view model is active.
   */
  private readonly _active = new BehaviorSubject<boolean>(false)

  /**
   * Team member to add as a supporting client.
   */
  @observable client?: Member | null = undefined

  /**
   * Disables submit button.
   */
  @observable disabled = true

  /**
   * Validation context for View Model.
   */
  readonly validation = validationContext(this, {
    client: [required('Collaborator is required')],
  })

  constructor(
    private job: Job,
    private jobStore: JobStore,
    private findTeamMembers: FindTeamMembers,
    private newAlert: AlertFactory,
  ) {
    super()

    makeObservable(this)

    this.close$.subscribe(this.reset)
    this.show$.subscribe(this.shown)
  }

  /**
   * Shows the dialog.
   */
  show() {
    if (this.showing) {
      return
    }

    super.show()
  }

  /**
   * Sets the client to add.
   */
  @action.bound
  setClient(client?: Member | null) {
    this.client = client
    this.disabled = !this.validate()
  }

  /**
   * Clears the fields of the View Model.
   */
  @action.bound
  reset() {
    this.client = undefined
    this.disabled = true

    this.submit.reset()
    this.validation.reset()
    this._active.next(false)
  }

  /**
   * Activates the member search field with a debounce time.
   */
  @action.bound
  shown() {
    this._active.next(true)
  }

  /**
   * Indicates if the View Model is valid.
   */
  @action.bound
  validate() {
    return this.validation.reset().validate().isValid
  }

  /**
   * Adds the supporting client.
   */
  @action.bound
  async addSupportingClient() {
    const alert = this.newAlert().withState(AlertState.Pending).show()

    if (!this.job.canAddClientsAsTeamMember) {
      alert
        .withState(AlertState.Error)
        .saying('You do not have permission to perform this action.')
        .autoDismiss()
      return
    }

    if (!this.validate()) {
      return
    }

    alert
      .withState(AlertState.Pending)
      .saying(
        'Adding Client',
        `Adding ${this.client?.displayName} to ${this.job.name}...`,
      )
      .autoDismiss()
      .show()

    await this.jobStore.addTeamMemberAsSupportingClient(
      Id.assert(this.job.id),
      Id.assert(this.client?.user.publicId),
    )

    alert
      .withState(AlertState.Success)
      .saying(
        'Success!',
        `${this.client?.displayName} was added to ${this.job.name}.`,
      )
      .autoDismiss()

    this.close()
  }

  /**
   * submits the new collaborator depending on the type and role.
   */
  submit = task.resolved(async () => {
    return this.addSupportingClient()
  })

  /**
   * Fetches team members.
   */
  fetchTeamMembers = async (
    _searchText: string,
    params: CursorPageQuery,
  ): Promise<CursorPaged<Member | null>> => {
    if (!this.job.ownerTeam) {
      return CursorPaged.empty
    }

    const collaborators = this.job.collaborators
    const paged = await this.findTeamMembers({
      after: params.after,
      limit: params.limit,
      team_id: Id.assert(this.job.ownerTeam.id),
    })

    return CursorPaged.map(paged, (data) =>
      data
        .filter((m) => {
          const invalidCandidate = collaborators.some(
            (s) => s.member === m.member,
          )
          return !invalidCandidate
        })
        .map((m) => m.member),
    )
  }
}
